import React, { PureComponent } from "react";
import Select from "react-select";
import dataProvider, { GET_LIST } from "providers/dataProvider";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import { withStyles } from "@mui/styles";
import { ArrowDropDown } from "@mui/icons-material";
import withAutoFill from "./withAutoFill";
import Chip from "@mui/material/Chip";
import CancelIcon from "@mui/icons-material/Cancel";
import clsx from "clsx";
import style from "./Autocomplete.module.scss";
import { find, isArray, get } from "lodash";
import { withAuthCenter } from "AuthCenter";
import { compose } from "redux";
import axios from "axios";

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    zIndex: 20,
  }),
  clearIndicator: () => ({
    ">svg": {
      width: "15px",
      height: "15px",
      margin: "3px 0 0",
    },
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    paddingRight: '18px',
    // gap: '4px',
  }),
  multiValue: (provided) => ({
    ...provided,
    margin: '0px',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    height: 250,
  },
  input: {
    display: "flex",
    padding: 0,
    height: "auto",
  },
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: "absolute",
    left: 2,
    bottom: 6,
    fontSize: 16,
    color: "red",
  },
  indicatorSeparator: {
    display: "none",
  },
  chip: {
    margin: "0px",
  },
});

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

inputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Control(props) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps },
  } = props;

  return (
    <TextField
      variant="standard"
      error={TextFieldProps.input.error}
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
}

const DropdownIndicator = (props) => {
  return <ArrowDropDown />;
};

Control.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  selectProps: PropTypes.object.isRequired,
};

const components = {
  Control,
  DropdownIndicator,
  MultiValue,
};

class Autocomplete extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...props,
      options: [],
      dataLoading: false,
      inputValue: "",
      dataLoaded: false,
      isDisabled: false,
    };

    this.loadDataAfterMenuOpen =
      get(this.props.input, "autocompleteOptions.loadDataAfterOpenMenu") ||
      false;
    this.forceLoadingDataWhenValueAvailable =
      get(
        this.props.input,
        "autocompleteOptions.forceLoadingDataWhenValueAvailable"
      ) || true;
    this.isClearable =
      get(this.props.input, "autocompleteOptions.isClearable") || false;

    this.CancelToken = axios.CancelToken;
    this.source = this.CancelToken.source();
  }

  componentDidMount() {
    if (this.props.input.value && this.forceLoadingDataWhenValueAvailable) {
      this.loadData();
    } else {
      if (!this.loadDataAfterMenuOpen) {
        this.loadData();
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.input.value === "" &&
      this.props.input.value !== "" &&
      this.forceLoadingDataWhenValueAvailable &&
      this.state.options.length === 0
    ) {
      this.loadData();
    }

    if (prevProps.value !== this.props.value) {
      this.setState({ value: this.props.value });
    }

    if (this.props.input.hasOwnProperty('relativeInputsValueAccessor') && this.props.input.hasOwnProperty('relativeInputsData')) {
      const newValue = this.props.input.relativeInputsValueAccessor(this.props.input.relativeInputsData);
      if (newValue) {
        const selectedOption = this.getSelectedOption(newValue);
        if (selectedOption && !this.state.isDisabled) {
          this.setState({ isDisabled: true });
          this.handleChange(selectedOption);
        }
      }
      if (!newValue && this.state.isDisabled) {
        this.setState({ isDisabled: false });
      }
    }
    if (
      JSON.stringify(this.props.input.relativeInputsData) !==
      JSON.stringify(prevProps.input.relativeInputsData)
    ) {
      if (!this.loadDataAfterMenuOpen) {
        this.loadData();
      }
    }
    if (prevProps.authCenter.token !== this.props.authCenter.token) {
      if (!this.loadDataAfterMenuOpen) {
        this.loadData();
      }
    }
  }

  componentWillUnmount() {
    this.cancelLoadData();
  }

  getFilter = (input) => {
    try {
      return (
        input.filter ||
        (input.relativeInputsFilterAccessor &&
          input.relativeInputsFilterAccessor(input.relativeInputsData)) ||
        {}
      );
    } catch {
      return {};
    }
  };

  getResource = (input, options) => {
    try {
      const relData = input.relativeInputsData || options; // fallback to smartInputOptions
      return (
        input.resource ||
        (input.resourceAccessor && input.resourceAccessor(options)) ||
        (input.relativeInputResourceAccessor && input.relativeInputResourceAccessor(relData, options))
      );
    } catch {
      return false;
    }
  };

  cancelLoadData() {
    this.source.cancel();
    this.source = this.CancelToken.source();
  }

  loadData() {
    const { input, options } = this.props;
    this.cancelLoadData();
    this.setState({
      options: [],
    });

    const filter = this.getFilter(input) || {};
    const resource = this.getResource(input, options);

    if (!resource) return;

    this.setState({
      dataLoading: true,
    });

    dataProvider(
      GET_LIST,
      resource,
      {
        filter: filter,
        pagination: {
          perPage: -1,
        },
        sort: input.sort,
      },
      {
        cancelToken: this.source.token,
      }
    )
      .then(({ data, total }) => {
        let options = data && Array.isArray(data) && input.accessor ? data.map(input.accessor) : [];
        this.setState(
          {
            dataLoaded: true,
            dataLoading: false,
            options,
          },
          () => {
            // Auto-populate if there is exactly one option and no value is already set
            // AND this is specifically the labId field
            if (options.length === 1 && !this.props.input.value && this.props.inputId === 'labId') {
              console.log('Auto-selecting the only lab option:', options[0]);
              this.handleChange(options[0]);
            }
            // Call setInitValue to handle any initial value setup
            if (data) {
              this.setInitValue(data);
            }
          }
        );
      })
      .catch((e) => {
        console.error("Error loading data:", e);
        this.setState({
          dataLoading: false,
          dataLoaded: true,
          options: [],
        });
      });
  }

  setInitValue = (rows) => {
    const { input } = this.props;
    if (!input.hasOwnProperty("setInitValueFromData")) return;

    const defaultSetInitValueFromData = {
      inputKey: null,
      outputKey: null,
      value: null,
      forceUpdate: false,
    };

    const setInitValueFromData = {
      ...defaultSetInitValueFromData,
      ...input.setInitValueFromData,
    };

    if (input.value && !setInitValueFromData.forceUpdate) return;

    const row = rows.find(
      (row) => row[setInitValueFromData.inputKey] === setInitValueFromData.value
    );
    if (row) {
      const response = row[setInitValueFromData.outputKey];
      this.handleChange({ value: response });
    }
  };

  handleChangeMulti = (selectedOption) => {
    if (!selectedOption) {
      this.props.handleInputs({
        target: { value: [], name: this.props.inputId },
      });
      return;
    }
    const updatedSelectedOption = selectedOption.map((option) => {
      if (option.hasOwnProperty("value")) {
        return option.value;
      } else {
        return option;
      }
    });

    let e = {
      target: { value: updatedSelectedOption, name: this.props.inputId },
    };
    this.props.handleInputs(e);
  };

  handleChange = (selectedOption, options = {}) => {
    const { action = "select-option" } = options;

    if (action === "clear") {
      let e = { target: { value: null, name: this.props.inputId } };
      this.props.handleInputs(e);
    } else if (action === "select-option") {
      let extraSelectedOptionData = null;

      if (selectedOption.relatedValue) {
        this.props.input.relatedValue = selectedOption.relatedValue;
      }
      extraSelectedOptionData = selectedOption.extraSelectedOptionData;
      let e = {
        target: { value: selectedOption.value, name: this.props.inputId },
      };
      this.props.handleInputs(e, extraSelectedOptionData, selectedOption.label);
    }
  };

  onInputChange = (inputValue, { action }) => {
    if (action !== "set-value") {
      this.setState({ inputValue });

      return inputValue;
    }

    return this.state.inputValue;
  };

  onOpen = () => {
    if (!this.state.dataLoaded) {
      this.loadData();
    }
  };

  getSelectedOption = (value) => {
    let selectedOption = null;

    const { input } = this.props;
    const { options } = this.state;

    if (
      input.subType === "multi" &&
      value &&
      isArray(value) > 0 &&
      options.length > 0
    ) {
      selectedOption = value.map((option) => {
        if (option.hasOwnProperty("value")) {
          return option;
        } else {
          return {
            value: option,
            label: find(options, ["value", option]).label,
          };
        }
      });
    } else if (input.subType !== "multi" && value) {
      // Support both primitive and object values
      if (typeof value === 'object' && value !== null && value.hasOwnProperty('value')) {
        // If options are loaded, prefer the option object from options to keep refs consistent
        const fromOptions = options.find((d) => d.value === value.value);
        selectedOption = fromOptions || value;
      } else {
        selectedOption = options.find((d) => d.value === value);
      }
    }

    return selectedOption;
  };

  render() {
    const { classes, input, inputId, value } = this.props;
    const { options, dataLoading, isDisabled } = this.state;
    const isDataLoaded = true;

    const selectedOption = this.getSelectedOption(value);

    return (
      <div
        className={clsx(style.root, this.props.style.textField, {
          [style.rootDisabled]: !isDataLoaded,
        })}
      >
        <Select
          {...input.selectProps}
          isDisabled={!isDataLoaded || input.disabled || isDisabled}
          isClearable={this.isClearable}
          value={selectedOption}
          menuPortalTarget={document.body}
          onChange={
            input.subType === "multi"
              ? this.handleChangeMulti
              : this.handleChange
          }
          onFocus={this.onOpen}
          isLoading={dataLoading}
          name={inputId}
          TextFieldProps={{
            required: input.hasOwnProperty("yup"),
            input: input,
            name: inputId,
            label: input.error ? input.errorMessage : input.label,
            InputLabelProps: {
              htmlFor: "react-select-single",
              shrink: true,
            },
            sx: {
              // Ensure label is floated and not overlapped by global overrides
              '& .MuiInputLabel-formControl': {
                transform: 'translate(0, -1.5px) scale(0.75) !important',
                letterSpacing: 0,
                fontSize: '0.8571428571428571rem',
                lineHeight: 1,
              },
              '& .MuiFormLabel-root': {
                fontSize: '0.8571428571428571rem',
                lineHeight: 1,
              },
              '& .MuiInputBase-root': {
                fontSize: '0.8571428571428571rem',
                lineHeight: '1.1876em',
                letterSpacing: 0,
              },
            },
          }}
          closeMenuOnSelect={!(input.subType === "multi")}
          components={components}
          options={options}
          classes={classes}
          isMulti={input.subType === "multi"}
          styles={customStyles}
          onInputChange={this.onInputChange}
        />
      </div>
    );
  }
}

Autocomplete.defaultProps = {
  style: {
    textField: "",
  },
};

export default compose(
  withAutoFill,
  withStyles(styles),
  withAuthCenter()
)(Autocomplete);